import {
  Component,
  ChangeDetectionStrategy,
  TemplateRef,
  ViewChild,
  effect,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbTagModule,
  NbToastrService,
} from '@nebular/theme';
import { WarrantyCertificateService } from '../../../../@core/services/warranty-certificate.service';

/**
 * Warranty certificate section for the equipment detail page.
 *
 * Shows the certificates that apply to the unit (direct association or
 * make/model/model-year catalog match). When none apply, offers an upload
 * flow that creates a certificate directly associated to this unit and
 * queues async AI processing (coverage summary + structured extraction).
 */
@Component({
  selector: 'next-warranty-certificate-section',
  templateUrl: './warranty-certificate-section.component.html',
  styleUrls: ['./warranty-certificate-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    NbButtonModule,
    NbCardModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    NbTagModule,
  ],
})
export class WarrantyCertificateSectionComponent {
  equipmentId = input.required<string>();
  /** Used to prefill the certificate name, e.g. "Toyota 8HBW23 2025". */
  suggestedName = input<string>('');

  certificates = signal<any[]>([]);
  loading = signal(false);
  uploading = signal(false);
  selectedFile = signal<File | null>(null);
  certificateName = signal('');

  @ViewChild('uploadDialog') uploadDialog: TemplateRef<any>;

  constructor(
    private warrantyCertificateService: WarrantyCertificateService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
  ) {
    effect(() => {
      const id = this.equipmentId();
      if (id) {
        this.loadCertificates(id);
      }
    });
  }

  loadCertificates(equipmentId: string) {
    this.loading.set(true);
    this.warrantyCertificateService
      .getWarrantyCertificatesByEquipment(equipmentId)
      .subscribe({
        next: (certificates) => {
          this.certificates.set(certificates);
          this.loading.set(false);
        },
        error: () => {
          this.certificates.set([]);
          this.loading.set(false);
        },
      });
  }

  openUploadDialog() {
    this.selectedFile.set(null);
    this.certificateName.set(this.suggestedName() || '');
    this.dialogService.open(this.uploadDialog);
  }

  onFileSelected(event: any) {
    const file: File = event.target?.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      this.toastrService.danger('Only PDF files are allowed', 'Invalid File');
      event.target.value = '';
      return;
    }
    this.selectedFile.set(file);
    if (!this.certificateName().trim()) {
      this.certificateName.set(file.name.replace(/\.pdf$/i, ''));
    }
  }

  fileSizeMB(file: File): string {
    return (file.size / 1024 / 1024).toFixed(2);
  }

  uploadCertificate(dialogRef: NbDialogRef<any>) {
    const file = this.selectedFile();
    const name = this.certificateName().trim();
    if (!file || !name) return;

    this.uploading.set(true);
    this.warrantyCertificateService
      .createWarrantyCertificateForEquipment(
        { name, fileName: file.name },
        this.equipmentId(),
      )
      .subscribe({
        next: (certificate) => {
          this.warrantyCertificateService
            .uploadPdfMultipart(certificate.id, file)
            .subscribe({
              next: () => {
                this.uploading.set(false);
                dialogRef.close();
                this.toastrService.success(
                  'Certificate uploaded. AI coverage summary is being generated.',
                  'Warranty Certificate',
                );
                this.loadCertificates(this.equipmentId());
              },
              error: (error) => {
                this.uploading.set(false);
                this.toastrService.danger(
                  error?.message || 'Upload failed',
                  'Warranty Certificate',
                );
              },
            });
        },
        error: (error) => {
          this.uploading.set(false);
          this.toastrService.danger(
            error?.message || 'Failed to create certificate',
            'Warranty Certificate',
          );
        },
      });
  }

  statusTagStatus(status: string): string {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Error':
        return 'danger';
      case 'Disabled':
        return 'basic';
      default:
        return 'info'; // Queued / Processing
    }
  }
}
